import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import {
  ELEVATED_DIALOG_TOOLTIP_Z_INDEX_OFFSET,
  ELEVATED_DIALOG_Z_INDEX_OFFSET,
} from "../../constants/dialogZIndex";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import type { AssessmentRegulation } from "../../services/assessmentService";
import { withoutBackdropClose } from "../../utils/withoutBackdropClose";

export type CompanyScopeDialogVariant = "partial" | "all";

export type CompanyScopeAlreadyAssessedDialogProps = Readonly<{
  open: boolean;
  variant: CompanyScopeDialogVariant;
  onClose: () => void;
  /** Count of regulations already assessed (from preview). */
  alreadyCount: number;
  /** Total regulations the user selected (checked rows). */
  selectedTotal: number;
  alreadyRunRegulations: AssessmentRegulation[];
  onRunAnyway: () => void;
  /** Partial variant only */
  onSkipAlreadyRun?: () => void;
  /** Disables action buttons while preview / task APIs are in flight */
  actionsDisabled?: boolean;
}>;

function formatRegLabel(r: AssessmentRegulation): string {
  return r.title?.trim() ? r.title : r.id;
}

/** Visible in dialog: [INS163636...] when multiple or id is long. */
export function formatBracketRegulationId(
  id: string,
  options: Readonly<{ implyMore?: boolean }> = {},
): string {
  const { implyMore = false } = options;
  const needsEllipsis = implyMore || id.length > 12;
  const inner = needsEllipsis
    ? `${id.length > 12 ? id.slice(0, 12) : id}...`
    : id;
  return `[${inner}]`;
}

/** Tooltip: full title(s); multiple regs listed line by line. */
export function buildAlreadyRunRegulationsTooltip(
  regulations: readonly AssessmentRegulation[],
): string {
  if (regulations.length === 0) return "";
  return regulations
    .map((r) => {
      const label = formatRegLabel(r);
      return label === r.id ? r.id : `${r.id} — ${label}`;
    })
    .join("\n");
}

const REG_ID_LINK_SX = {
  fontWeight: 600,
  cursor: "help",
  color: "primary.main",
  textDecoration: "underline",
  textDecorationStyle: "dotted" as const,
  textUnderlineOffset: 3,
  "&:hover": {
    color: "primary.dark",
    textDecorationColor: "primary.dark",
  },
};

const REGULATION_ID_TOOLTIP_SLOT_PROPS = {
  popper: {
    sx: {
      zIndex: (theme: { zIndex: { modal: number } }) =>
        theme.zIndex.modal +
        ELEVATED_DIALOG_Z_INDEX_OFFSET +
        ELEVATED_DIALOG_TOOLTIP_Z_INDEX_OFFSET,
    },
  },
  tooltip: {
    sx: {
      whiteSpace: "pre-line",
      maxWidth: 360,
    },
  },
} as const;

function RegulationIdHoverChip({
  regulations,
}: Readonly<{
  regulations: readonly AssessmentRegulation[];
}>) {
  if (regulations.length === 0) return null;

  const first = regulations[0];
  const bracket = formatBracketRegulationId(first.id, {
    implyMore: regulations.length > 1,
  });
  const tooltip = buildAlreadyRunRegulationsTooltip(regulations);

  return (
    <Tooltip
      title={tooltip}
      placement="top"
      enterDelay={200}
      slotProps={REGULATION_ID_TOOLTIP_SLOT_PROPS}
    >
      <Typography component="span" sx={REG_ID_LINK_SX} tabIndex={0}>
        {bracket}
      </Typography>
    </Tooltip>
  );
}

export default function CompanyScopeAlreadyAssessedDialog({
  open,
  variant,
  onClose,
  alreadyCount,
  selectedTotal,
  alreadyRunRegulations,
  onRunAnyway,
  onSkipAlreadyRun,
  actionsDisabled = false,
}: CompanyScopeAlreadyAssessedDialogProps) {
  const partialBody =
    alreadyCount > 0 ? (
      <>
        {alreadyCount} of the {selectedTotal} selected regulations have already been
        assessed
        {alreadyRunRegulations.length > 0 ? (
          <>
            {": "}
            <RegulationIdHoverChip regulations={alreadyRunRegulations} />
          </>
        ) : null}
        . You can skip these {alreadyCount} regulations or run the assessment again for
        them.
      </>
    ) : null;

  const allBody =
    alreadyRunRegulations.length > 0 ? (
      <>
        Selected regulation have already been assessed:{" "}
        <RegulationIdHoverChip regulations={alreadyRunRegulations} />
        . You can skip these {alreadyCount} regulations or run the assessment again for
        them.
      </>
    ) : (
      "Selected regulations have already been assessed."
    );

  const title =
    variant === "partial"
      ? `${alreadyCount} Regulations Already Assessed`
      : "This Regulations Already Assessed";

  return (
    <Dialog
      open={open}
      onClose={withoutBackdropClose(onClose)}
      maxWidth="sm"
      fullWidth
      slotProps={{
        root: {
          sx: {
            zIndex: (theme) => theme.zIndex.modal + ELEVATED_DIALOG_Z_INDEX_OFFSET,
          },
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 600 }}>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
          {variant === "partial" ? partialBody : allBody}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          variant="outlined"
          disabled={actionsDisabled}
          onClick={onClose}
          sx={{ textTransform: "none" }}
        >
          Cancel
        </Button>
        {variant === "partial" && onSkipAlreadyRun && (
          <Button
            variant="outlined"
            disabled={actionsDisabled}
            onClick={onSkipAlreadyRun}
            sx={{ textTransform: "none" }}
          >
            Skip {alreadyCount}
          </Button>
        )}
        <Button
          variant="contained"
          disabled={actionsDisabled}
          onClick={onRunAnyway}
          sx={{ textTransform: "none" }}
        >
          Run Anyway
        </Button>
      </DialogActions>
    </Dialog>
  );
}
